import { supabase } from '../lib/supabaseClient';

/**
 * Delete user account and all associated data
 * This includes:
 * - All stored files in Supabase Storage
 * - All database records (surveys, properties, teams, etc.)
 * - User metadata
 * - The user account itself
 */
export const deleteUserAccount = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('[Account Deletion] Starting account deletion for user:', userId);

    // 1. Delete all files from storage buckets
    console.log('[Account Deletion] Step 1: Deleting storage files...');
    
    // Get user email for email-based paths
    const { data: { user } } = await supabase.auth.getUser();
    const userEmail = user?.email;
    
    // Priority buckets to check (includes all known buckets)
    const priorityBuckets = [
      'inventory-photos',
      'floorplan-images',
      'retrofit-survey-images',
      'retrofit-survey-files'
    ];
    
    // List all buckets
    const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
    
    if (bucketsError) {
      console.error('[Account Deletion] Error listing buckets:', bucketsError);
    } else if (buckets) {
      // For each bucket, try to delete user's files
      for (const bucket of buckets) {
        try {
          console.log(`[Account Deletion] Checking bucket: ${bucket.name}`);
          
          // Sanitize email for path matching
          const sanitizedEmail = userEmail 
            ? userEmail.toLowerCase().replace(/@/g, '-at-').replace(/\./g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')
            : null;
          
          // List files in the bucket for this user
          // Try common patterns: email-based (new) and user_id-based (old)
          const userPaths = [
            sanitizedEmail ? `${sanitizedEmail}/` : null,  // NEW: email-based path
            `${userId}/`,                                    // OLD: user_id path
            `user_${userId}/`,                               // OLD: user_userId path
            `users/${userId}/`,                              // OLD: users/userId path
          ].filter(Boolean) as string[];
          
          for (const path of userPaths) {
            try {
              // Recursive function to delete all files in a folder and subfolders
              const deleteFolder = async (folderPath: string): Promise<number> => {
                let totalDeleted = 0;
                
                // List all items in this folder
                const { data: items, error: listError } = await supabase.storage
                  .from(bucket.name)
                  .list(folderPath);
                
                if (listError) {
                  console.log(`[Account Deletion] Could not list ${bucket.name}/${folderPath}: ${listError.message}`);
                  return 0;
                }
                
                if (!items || items.length === 0) {
                  return 0;
                }
                
                console.log(`[Account Deletion] Found ${items.length} items in ${bucket.name}/${folderPath}`);
                
                // Separate files from folders
                const files = items.filter(item => item.id); // Files have an id
                const folders = items.filter(item => !item.id); // Folders don't have an id
                
                // Delete files in this folder
                if (files.length > 0) {
                  const filePaths = files.map(file => `${folderPath}${file.name}`);
                  console.log(`[Account Deletion] Deleting ${filePaths.length} files from ${bucket.name}/${folderPath}`);
                  
                  const { error: deleteError } = await supabase.storage
                    .from(bucket.name)
                    .remove(filePaths);
                  
                  if (deleteError) {
                    console.error(`[Account Deletion] Error deleting files from ${bucket.name}/${folderPath}:`, deleteError);
                  } else {
                    totalDeleted += filePaths.length;
                    console.log(`[Account Deletion] ✅ Deleted ${filePaths.length} files from ${bucket.name}/${folderPath}`);
                  }
                }
                
                // Recursively delete subfolders
                for (const folder of folders) {
                  const subfolderPath = `${folderPath}${folder.name}/`;
                  const subDeleted = await deleteFolder(subfolderPath);
                  totalDeleted += subDeleted;
                }
                
                return totalDeleted;
              };
              
              // Start recursive deletion
              const deletedCount = await deleteFolder(path);
              if (deletedCount > 0) {
                console.log(`[Account Deletion] ✅ Total deleted from ${bucket.name}/${path}: ${deletedCount} files`);
              }
            } catch (err) {
              console.error(`[Account Deletion] Error processing path ${bucket.name}/${path}:`, err);
            }
          }
          
          // Also try to delete files with user_id in filename
          const { data: allFiles } = await supabase.storage.from(bucket.name).list();
          if (allFiles) {
            const userFiles = allFiles.filter(file => 
              file.name.includes(userId) || file.name.includes(userId.replace(/-/g, '_'))
            );
            
            if (userFiles.length > 0) {
              const filePaths = userFiles.map(file => file.name);
              await supabase.storage.from(bucket.name).remove(filePaths);
              console.log(`[Account Deletion] ✅ Deleted ${filePaths.length} user-named files from ${bucket.name}`);
            }
          }
        } catch (err) {
          console.error(`[Account Deletion] Error processing bucket ${bucket.name}:`, err);
        }
      }
    }

    // 2. Call the delete_user RPC function
    // This handles all database deletions and bypasses RLS policies
    console.log('[Account Deletion] Step 2: Calling delete_user RPC function...');
    
    const { data: rpcResult, error: rpcError } = await supabase.rpc('delete_user');
    
    if (rpcError) {
      console.error('[Account Deletion] ❌ RPC function error:', rpcError);
      return {
        success: false,
        error: `Failed to delete account: ${rpcError.message}. Please contact support.`
      };
    }
    
    // Check the result from the RPC function
    if (rpcResult && !rpcResult.success) {
      console.error('[Account Deletion] ❌ Deletion failed:', rpcResult.error);
      return {
        success: false,
        error: `Failed to delete account: ${rpcResult.error}. Please contact support.`
      };
    }

    console.log('[Account Deletion] ✅ Account deletion complete!');
    return { success: true };

  } catch (error: any) {
    console.error('[Account Deletion] ❌ Unexpected error during account deletion:', error);
    return {
      success: false,
      error: `An unexpected error occurred: ${error.message || 'Unknown error'}`
    };
  }
};
